import { isPouchConflict } from '@cf-shared/data-model/pouch';

/** Check if an error message matches known IndexedDB connection failure patterns.
 * These occur when iOS/Safari closes IDB connections during backgrounding or offline transitions.
 * Exported for use by Sentry beforeSend hook. */
// The closed-handle messages below ('database is closed', "Can't start a transaction on a closed
// database") are also matched on their own by `isClosedDatabaseHandleError`, which callers use to
// tell "we called a handle we had closed" apart from a real device fault. Add a closed-handle
// message to one and add it to the other: dropping it from the narrow predicate sends that message
// back to being an unowned unhandled rejection.
export function isIdbConnectionError(errorMessage: string): boolean {
  return errorMessage.includes("Failed to execute 'transaction' on 'IDBDatabase'") ||
         errorMessage.includes('The database connection is closing') ||
         errorMessage.includes('without an in-progress transaction') ||
         errorMessage.includes('Attempt to get an index record') ||
         errorMessage.includes('Attempt to get a record') ||
         errorMessage.includes('Connection to Indexed Database server lost') ||
         errorMessage.includes('The I/O read operation failed') ||
         errorMessage.includes('database is closed') ||
         errorMessage.includes("Can't start a transaction on a closed database");
}

/** Check if an error object is an IndexedDB error requiring recovery.
 * Covers both PouchDB's synthetic `indexed_db_went_bad` (which has message: "unknown")
 * and raw IDB errors with specific message patterns. */
export function isIdbError(error: any): boolean {
  if (error?.name === 'indexed_db_went_bad') return true;
  return typeof error?.message === 'string' && isIdbConnectionError(error.message);
}

/** Matches the messages that mean "a call was made on a store handle that was already closed".
 *
 * This is a deliberately narrow subset of `isIdbError`. The difference matters: most of what
 * `isIdbError` matches is a DEVICE fault — the browser lost its IndexedDB connection, a read
 * failed, the store went bad — and the app is right to treat those as real failures worth
 * recovering from. What is matched here is not a device fault at all. PouchDB produces these
 * messages from its own guard when a fresh call arrives on a handle whose `close()` has already
 * finished, so they say something about OUR lifecycle (a database was shut down and something
 * still called it) and nothing about the machine the app is running on.
 *
 * Keeping those apart is what lets a caller own a late call against a closed handle without
 * also swallowing a genuinely broken IndexedDB. */
export function isClosedDatabaseHandleError(error: any): boolean {
  if (typeof error?.message !== 'string') return false;
  return error.message.includes('database is closed')
    || error.message.includes("Can't start a transaction on a closed database");
}

/** iOS WKWebView surfaces NSURLErrorDomain connectivity drops not as a "Failed to fetch" /
 * "Load failed" TypeError but as a plain `Error` (name "Error") whose message is one of these
 * fixed phrases — e.g. "The network connection was lost." (NSURLErrorNetworkConnectionLost,
 * -1005). These are pure connectivity-loss conditions and are always transient (the connection
 * is back moments later — a `ping.svg` probe right after the failure succeeds). We match by
 * substring (case-insensitive) so the leading "The " / trailing "." don't have to be exact.
 * Timeouts are deliberately NOT in this list — a slow server is a different signal, and the
 * sibling `isTransientFetchError` test asserts a TimeoutError stays non-transient.
 * "A server with the specified hostname could not be found." (NSURLErrorCannotFindHost, -1003)
 * is the DNS failure, the iOS twin of Android's "Unable to resolve host" (#6086).
 * Phrases are lowercase. This list is the one source: `NETWORK_SYNC_ERROR_SHAPE`
 * (sync-failure-classification.ts) is built from it, so a phrase added here is
 * recognized by the sync-failure checks too. */
export const IOS_CONNECTIVITY_LOST_PHRASES: readonly string[] = [
  'the network connection was lost',
  'the internet connection appears to be offline',
  'a data connection is not currently allowed',
  'a server with the specified hostname could not be found',
];

/** True when the message contains one of the iOS connectivity-loss phrases above. */
function isIosConnectivityLostMessage(message: string): boolean {
  const lower = message.toLowerCase();
  return IOS_CONNECTIVITY_LOST_PHRASES.some(phrase => lower.includes(phrase));
}

/** Android sibling of `isIosConnectivityLostMessage` (#4010 hygiene, from the #4014
 * investigation). CapacitorHttp on Android surfaces network-layer failures as plain
 * `Error` objects (name "Error") whose message is the raw OS socket-layer text:
 * a DNS failure reads `Unable to resolve host "<host>": No address associated with
 * hostname`, and a torn-down connection reads `Software caused connection abort`
 * (ECONNABORTED). Before these were recognized, a transient mobile network flap
 * during couch login escaped every network-error classifier and was reported to
 * Sentry as an AUTH failure ("Auth: Couch login - too many retries" APP-3WY /
 * "CouchAuth failed when coming online: CouchDB authentication failed" APP-3Z9).
 * #6086 added the OkHttp texts for the other ways a connection fails: the connect itself
 * failed (`Failed to connect to <host>/<address>:443`), the socket was reset or closed
 * (`Connection reset`, `connection closed`), or it dropped part-way through the response
 * (`unexpected end of stream on <connection>`). Each was filed in Sentry as
 * CouchSessionPostFailed (APP-443) while the phone was offline.
 * Same substring/case-insensitive matching rules as the iOS family; timeouts stay
 * deliberately excluded (see the iOS comment). Like the iOS list, this is the one source
 * that `NETWORK_SYNC_ERROR_SHAPE` is built from. */
export const ANDROID_CONNECTIVITY_LOST_PHRASES: readonly string[] = [
  'unable to resolve host',
  'software caused connection abort',
  'failed to connect to',
  'connection reset',
  'connection closed',
  'unexpected end of stream',
];

/** True when the message contains one of the Android connectivity-loss phrases above. */
function isAndroidConnectivityLostMessage(message: string): boolean {
  const lower = message.toLowerCase();
  return ANDROID_CONNECTIVITY_LOST_PHRASES.some(phrase => lower.includes(phrase));
}

/** The combined native connectivity-loss message family (iOS + Android). */
function isNativeConnectivityLostMessage(message: string): boolean {
  return isIosConnectivityLostMessage(message) || isAndroidConnectivityLostMessage(message);
}

/** Check if an error is a transient fetch/network error (offline, timeout, backgrounding).
 * Covers: offline "Failed to fetch" / "Load failed", AbortController timeouts, Safari backgrounding
 * aborts, and the native connectivity-loss message families — iOS NSURLErrorDomain
 * (`isIosConnectivityLostMessage`) and Android socket-layer (`isAndroidConnectivityLostMessage`).
 * Accepts Error objects or string messages (for Sentry beforeSend hook).
 * Uses startsWith for BOTH "Failed to fetch" and "Load failed" because Sentry SDK v10
 * enriches the message with the host — e.g. "Failed to fetch (hostname:port)" and, on iOS
 * WKWebView, "Load failed (couch-prod.db.mh-physique.app)". An exact "=== 'Load failed'"
 * check misses the enriched iOS form, which is the most common native read failure. */
export function isTransientFetchError(error: unknown): boolean {
  if (error instanceof DOMException && error.name === 'AbortError') return true;
  if (error instanceof TypeError) {
    return error.message.startsWith('Failed to fetch') || error.message.startsWith('Load failed')
      || error.message.startsWith('NetworkError when attempting to fetch resource');
  }
  if (typeof error === 'string') {
    return error.startsWith('Failed to fetch') || error.startsWith('Load failed')
      || error.startsWith('signal is aborted') || error.startsWith('Fetch is aborted')
      || isNativeConnectivityLostMessage(error);
  }
  // Plain objects {message, name} that aren't fetch TypeErrors: CapacitorHttp on Android throws
  // these, and iOS WKWebView surfaces an NSURLErrorDomain drop as a generic `Error` (name "Error",
  // not a TypeError). Match either by message — note this branch now also accepts `Error` instances
  // (the iOS connectivity-loss case), so it intentionally has no `!(error instanceof Error)` guard.
  if (typeof error === 'object' && error !== null) {
    // an aborted fetch (90s per-request timeout, PouchDB sync-cancel, or Safari backgrounding)
    // can reach us as a plain object rather than a DOMException — PouchDB's get() augments the
    // abort with a `docId` property, which produces a plain `{ name: 'AbortError', message,
    // docId }`. The DOMException branch above misses that shape, so match the name directly here,
    // mirroring the string branch's 'Fetch is aborted' / 'signal is aborted' handling. An
    // AbortError is, by its nature, always a transient abort — safe to classify as transient.
    if ((error as any).name === 'AbortError') return true;
    const msg = (error as any).message;
    return typeof msg === 'string'
      && (msg.startsWith('Failed to fetch') || msg.startsWith('Load failed')
        || msg.startsWith('Fetch is aborted') || msg.startsWith('signal is aborted')
        || isNativeConnectivityLostMessage(msg));
  }
  return false;
}

/** The queue-vs-lose classification for image uploads (#3989): true when a
 * failed upload step means "the network or server was unreachable" — the
 * image upload outbox's signal to queue the photo (enqueue path) or to skip
 * an attempt increment (drain path). False means a real failure that must
 * alert/report or count toward the give-up cap.
 *
 * Two layers are covered because upload steps fail through two different
 * shapes:
 * - cloud-function calls (signed URL / download URL) exhaust their retries
 *   and throw a wrapped `CloudFunctionNetworkError`
 *   (cloud-functions.service.ts). Matched by NAME, not instanceof, so this
 *   module stays free of that Angular-heavy import graph and remains
 *   node-loadable for its unit test (error-patterns.test.ts). The wrapper
 *   already absorbed timeouts/retries, so no separate timeout arm is needed
 *   here — and `isTransientFetchError` deliberately keeps raw timeouts
 *   non-transient (see its comment).
 * - the raw `fetch` PUT to GCS throws bare fetch errors (TypeError /
 *   AbortError / the iOS "connection was lost" family), which
 *   `isTransientFetchError` covers.
 * Using either layer alone misclassifies the other layer's failure — the
 * wrapped form was missed in the first #3989 review cycle and silently
 * disabled the whole enqueue path for the common "no connection at all"
 * case. */
export function isOfflineOrTransientNetworkError(error: unknown): boolean {
  if (isTransientFetchError(error)) return true;
  return typeof error === 'object' && error !== null
    && (error as { name?: string }).name === 'CloudFunctionNetworkError';
}

/** Check if an error is a Firebase auth network failure (auth/network-request-failed).
 * This is a transient offline / flaky-network condition during Firebase auth (token
 * refresh, or the auth iframe / reCAPTCHA path). The app handles auth offline
 * gracefully (deferred couch login + focus/online retry), so these should not surface
 * as Sentry errors — the iframe path otherwise escapes as an unhandled rejection.
 * Accepts FirebaseError-like objects (with `.code`) or string messages. */
export function isFirebaseAuthNetworkError(error: unknown): boolean {
  // match with `includes` (not the `startsWith` the sibling fetch predicate uses): Firebase
  // wraps the code mid-message, e.g. "Firebase: Error (auth/network-request-failed)."; the
  // token is a distinctive error code, so over-matching is not a real risk
  if (typeof error === 'string') return error.includes('auth/network-request-failed');
  if (typeof error === 'object' && error !== null) {
    if ((error as any).code === 'auth/network-request-failed') return true;
    const msg = (error as any).message;
    return typeof msg === 'string' && msg.includes('auth/network-request-failed');
  }
  return false;
}

/** The wording the Refiner survey widget uses when its script cannot be fetched — almost always
 * an ad-blocker or a content blocker stopping the CDN request. Nothing in the app depends on the
 * widget loading, and the next page load tries again, so this is suppressed rather than reported.
 *
 * Accepts either a plain message string or an error-like object carrying `.message`, because the
 * two places that ask are handed different shapes: the Sentry `beforeSend` hook tests a parsed
 * exception's `value` (a string), and the `unhandledrejection` listener tests the raw rejection
 * reason (an object). One predicate so the wording is written down once. */
export function isRefinerLoadError(error: unknown): boolean {
  if (typeof error === 'string') return error.includes('Could not load Refiner client');
  if (typeof error === 'object' && error !== null) {
    const msg = (error as { message?: unknown }).message;
    return typeof msg === 'string' && msg.includes('Could not load Refiner client');
  }
  return false;
}

/** Check if an error is a PouchDB document conflict. These are self-healing — replication and
 * `saveRecordInternalAsync` both resolve them by merging — so they are suppressed rather than
 * reported.
 *
 * Asks the shared `isPouchConflict` for the raw-error half, so a conflict that arrives as
 * `status: 409` with no `name` is suppressed here too. Before that, this predicate read only
 * `name`, so a conflict in that shape was reported to Sentry as a real fault.
 *
 * The extra `type` check is what keeps this wrapper rather than the shared predicate alone: one
 * of the two callers is handed a PARSED SENTRY EXCEPTION, which carries the same word in `type`
 * and has neither `name` nor `status`. Only one of the fields is ever present at a given call
 * site. */
export function isPouchConflictError(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) return false;
  const e = error as { type?: unknown };
  return isPouchConflict(error) || e.type === 'conflict';
}

/** Extract the identifying fields (name / code / message) a caught unknown error carries, for
 * use as (or spread into) a `trackSilentProblem` context so Sentry events group usefully.
 * One shared shape instead of hand-built `(err as {...})?.x` casts at every report site. */
export function errorReportFields(err: unknown): { name?: string; code?: string; message?: string } {
  const e = err as { name?: string; code?: string; message?: string } | null | undefined;
  return { name: e?.name, code: e?.code, message: e?.message };
}

/** The shape of a raw CouchDB/PouchDB HTTP error object (see `isCouchHttpErrorObject`). */
export interface CouchHttpErrorObject {
  status: number;
  name: string;
  error?: string;
  reason?: string;
  message?: string;
}

/** Check if a value is a raw CouchDB/PouchDB HTTP error object that is NOT an `Error`
 * instance — e.g. `{ status: 404, name: 'not_found', error: 'not_found', reason: 'missing',
 * message: 'missing' }`, or the 401/403 equivalents (`unauthorized` / `forbidden`).
 *
 * PouchDB/CouchDB report HTTP failures as plain objects, not `Error` instances. When such an
 * object escapes a fire-and-forget call as an unhandled promise rejection, Sentry has no real
 * message or stack to group on, so every one of them — whatever the cause — collapses into a
 * single useless `<unknown>` group ("Object captured as promise rejection with keys: error,
 * message, name, reason, stack", Sentry issue 2JB). The Sentry `beforeSend` hook uses this to
 * recognise that shape and rewrite the event into a real, per-(status, name) grouped error.
 *
 * Returns a type guard so callers get typed field access without `as any` casts. The
 * `!(error instanceof Error)` check is deliberate: a genuine `Error` that happens to carry a
 * `.status` already groups well by its own message/stack and must not be rewritten here. */
export function isCouchHttpErrorObject(error: unknown): error is CouchHttpErrorObject {
  if (typeof error !== 'object' || error === null || error instanceof Error) return false;
  const e = error as Record<string, unknown>;
  return typeof e.status === 'number' && e.status >= 400
    && typeof e.name === 'string'
    && (typeof e.error === 'string' || typeof e.reason === 'string');
}

/** The exact wording CouchDB uses when it refuses to authenticate an account whose
 * `_users` document holds conflicting revisions. Matched case-insensitively, and only on
 * the distinctive first half, so a trailing full stop or a reworded tail cannot break it.
 *
 * Exported so that `couch-fetch-conflict-retry.ts` can ask the same question of a raw
 * HTTP response body, which arrives as text rather than as a PouchDB error object. The
 * sentence itself must stay in ONE place in this package — it is already restated once in
 * the server package, which cannot import this one. */
export function isUsersDocConflictReason(text: string): boolean {
  return text.toLowerCase().includes('user document conflicts must be resolved');
}

/** Check if an error is CouchDB refusing to authenticate a brand-new account because its
 * `_users` document briefly holds conflicting revisions.
 *
 * What happens: for a few seconds after an account is created, two versions of that
 * person's login record can exist at once. CouchDB will not log the account in at all
 * while that is true — it answers `401 unauthorized` with the reason "User document
 * conflicts must be resolved before the document is used for authentication purposes."
 * The condition repairs itself: the client's fetch wrapper refreshes the session, which
 * runs the server-side heal (`resolveUserDocConflictsAsync`), and the next attempt
 * succeeds. The underlying race is tracked at cybsol/mh-physique#4761.
 *
 * Why a predicate of its own. A read made DURING that window fails with this 401, and
 * without a name for it the app treated it as an unexplained failure and showed the user
 * an error toast — for a problem they did not cause and that was already fixing itself
 * (deferred finding df-805d12d77cff).
 *
 * Why it matches the wire text. There is nothing else on the wire to key on: the status
 * and the `unauthorized` name are exactly what an ordinary wrong-password 401 carries
 * too, so matching those alone would reclassify real authentication failures. Matching a
 * third party's fixed message is the established pattern in this file — see
 * `isIosConnectivityLostMessage` and its Android sibling.
 *
 * The server matches the same sentence, separately, in `couch-user-session.ts`'s
 * `isUserDocConflictError` — the two packages cannot import each other. If CouchDB ever
 * rewords it, both stop matching, and neither says so.
 *
 * It deliberately inherits `isCouchHttpErrorObject`'s refusal to match `Error` instances.
 * The condition arrives from PouchDB as a plain object; a genuine `Error` whose message
 * happens to contain these words is somebody's own code and must keep reporting. */
export function isCouchUsersDocConflictError(error: unknown): boolean {
  if (!isCouchHttpErrorObject(error)) return false;
  if (error.status !== 401) return false;
  // CouchDB puts the sentence in `reason`; PouchDB copies it into `message` as well, so
  // read either rather than depending on which layer handed us the object.
  return isUsersDocConflictReason(error.reason ?? '') || isUsersDocConflictReason(error.message ?? '');
}

/** The shape of the parts of a Sentry event the third-party check reads.
 * Declared structurally rather than imported from `@sentry/angular`, so this module
 * stays free of the Angular-dependent Sentry package and remains unit-testable. */
export interface AppFrameLike { filename?: string }
export interface AppExceptionLike { stacktrace?: { frames?: AppFrameLike[] } }
export interface AppErrorEventLike { exception?: { values?: AppExceptionLike[] } }

/** Check if a Sentry event originates entirely from external/third-party scripts
 * (e.g. browser extensions, injected Android WebView overlays).
 * Returns true when every exception stacktrace contains ONLY frames whose filenames
 * are <anonymous>, [native code], or from non-app origins.
 * Events without any stacktraces (e.g. captureMessage) are NOT considered third-party.
 * `appOrigin` is the address the page itself is served from — see `getAppFrameOrigin`.
 * Exported for use by Sentry beforeSend hook. */
export function isThirdPartyError(event: AppErrorEventLike, appOrigin: string): boolean {
  // if we cannot work out where our own code is served from, suppress nothing.
  // Reporting noise is the cheap mistake; dropping a real crash is the expensive one.
  if (!appOrigin) return false;

  const exceptionValues = event.exception?.values;
  if (!exceptionValues || exceptionValues.length === 0) {
    return false;
  }

  let totalFrames = 0;
  let hasAnyStacktrace = false;

  for (const ex of exceptionValues) {
    const frames = ex.stacktrace?.frames;
    if (!frames || frames.length === 0) continue;
    hasAnyStacktrace = true;

    for (const frame of frames) {
      totalFrames++;
      // if any frame belongs to our app code, this is not a third-party error
      if (isAppFrame(frame.filename, appOrigin)) return false;
    }
  }

  // no stacktraces at all — could be our captureMessage, don't filter
  if (!hasAnyStacktrace) return false;

  // had frames but none were from our app
  return totalFrames > 0;
}

/** Check if a stack frame filename belongs to our app code (allow-list approach).
 * `appOrigin` is the address the page itself is served from, from `getAppFrameOrigin`. */
function isAppFrame(filename: string | undefined, appOrigin: string): boolean {
  if (!filename || filename === '<anonymous>' || filename === '[native code]') {
    return false;
  }
  // anything served from the same address as the page itself is our own bundle.
  // This is the rule that covers the phone apps, where Capacitor serves our page
  // from the device (`https://localhost` on Android, `capacitor://localhost` on
  // iOS) rather than from our website (#4546).
  //
  // Two details that look fussy and are not. The trailing slash stops
  // `https://localhost.evil.com/x.js` matching the Android app's address. And the
  // empty-string check stops an unknown address turning into a bare `/` prefix,
  // which would call every root-relative filename ours — the only caller already
  // returns before that can happen, so this guards a future second caller.
  if (appOrigin && filename.startsWith(`${appOrigin}/`)) return true;
  // production URLs (all environments: www, dev, canary, local)
  if (filename.includes('mh-physique.app')) return true;
  // webpack internal URLs (dev mode)
  if (filename.startsWith('webpack-internal://')) return true;
  // localhost dev server
  if (filename.includes('localhost:') || filename.includes('127.0.0.1:')) return true;
  return false;
}

/** The address the page itself is served from, in the form a stack-frame filename
 * carries it — `https://www.mh-physique.app`, `https://localhost` (Android app),
 * `capacitor://localhost` (iOS app), `http://localhost:4200` (dev server).
 * Answers the empty string when it cannot be worked out; the caller reads that as
 * "suppress nothing". */
export function getAppFrameOrigin(loc: { protocol?: string; host?: string } | undefined): string {
  const protocol = loc?.protocol;
  const host = loc?.host;
  if (!protocol || !host) return '';
  return `${protocol}//${host}`;
}
